import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

/**
 * ClockInOutFAB — Floating quick-access pill (bottom-right of viewport).
 *
 * History:
 *   Originally rendered TWO buttons: a "Clock in/out" toggle and a
 *   "📋 Register" shortcut. Staff fed back that the clock pill blocked
 *   other UI elements (action buttons in tables, modal close icons),
 *   so the clock toggle was removed in favour of the dedicated side-nav
 *   "🕒 Clock In/Out" item. The Register quick-jump remained because
 *   admins/drivers actively use it to swap into the daily roster
 *   while operating from another screen.
 *
 * Behaviour now:
 *   - Renders only the "📋 Register" pill (the clock toggle is gone).
 *   - Hides itself entirely when the user has no `staff_profile`
 *     (parents, desk-only org-owners) or is already on the roster tab.
 *   - Polls `/api/clock-events/my-status` once on mount purely to
 *     decide whether to show the pill — no clock action is triggered
 *     from this component any more.
 *
 * Naming: file/component name kept as-is to avoid churning the 3
 * dashboards that mount it; future refactor may rename it to
 * `RegisterQuickJumpFAB`.
 */
export function ClockInOutFAB({
  activeTab,
  onNavigate,
  showRegister = true,
  // Kept in the signature for API stability — no longer consumed.
  // eslint-disable-next-line no-unused-vars
  rooms = [],
}) {
  const [hasProfile, setHasProfile] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await api.getMyClockStatus();
      setHasProfile(!!s?.has_profile);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when the active tab changes — a user who was a parent at
  // mount but switched workspaces (rare) shouldn't get a stale pill.
  useEffect(() => { refresh(); }, [activeTab, refresh]);

  if (!hasProfile) return null;
  if (!showRegister) return null;

  // Already on the roster — hide so the pill doesn't sit on top of
  // the page's own primary action area.
  const hideRoster = activeTab === 'roster';
  if (hideRoster) return null;

  const handleRegister = () => {
    if (typeof onNavigate === 'function') onNavigate('roster');
  };

  return (
    <div
      data-testid="clock-fab-wrapper"
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '16px',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'flex-end',
        pointerEvents: 'none', // children re-enable so the surrounding area stays clickable
      }}
    >
      <button
        type="button"
        onClick={handleRegister}
        data-testid="clock-fab-register"
        style={{
          pointerEvents: 'auto',
          background: '#6366f1',
          color: '#fff',
          border: 'none',
          borderRadius: '999px',
          padding: '12px 20px',
          fontWeight: 700,
          fontSize: '0.9rem',
          boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'transform 150ms ease',
        }}
        title="Jump to the children's register"
      >
        📋 Register
      </button>
    </div>
  );
}

export default ClockInOutFAB;
